import { EarningsInsight } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import {
  SubTabsType,
  useEarningsInsightDrawer,
} from '~/widgets/earnings-insight-drawer/useEarningsInsightDrawer';

import { EarningsInsightIcon } from '../earnings-insight-icon/EarningsInsightIcon';
import { getEarningsInsightIconName } from '../earnings-insight-icon/utils';
import { EarningsInsightTooltip } from '../earnings-insight-tooltip/EarningsInsightTooltip';
import * as S from './EarningsInsightPill.styles';

export interface EarningsInsightPillProps {
  isPreview: boolean;
  entityTag: string;
  earningsInsight: EarningsInsight;
  isClickable?: boolean;
  source: PosthogTrackingSource;
}

export const EarningsInsightPill = ({
  isPreview,
  entityTag,
  earningsInsight,
  isClickable = true,
  source,
}: EarningsInsightPillProps) => {
  const { t } = useTranslation(['earnings']);
  const { openDrawer } = useEarningsInsightDrawer();

  return (
    <TooltipContainer
      label={
        <EarningsInsightTooltip
          earningsInsight={earningsInsight}
          isPreview={isPreview}
          iconProps={{
            iconName: getEarningsInsightIconName(isPreview, true),
            viewBox: '0 0 30 32',
          }}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!isClickable}
      inPortal
    >
      <S.EarningsInsightWrapper
        data-testid="earnings-insight-pill"
        $isPreview={isPreview}
        $isClickable={isClickable}
        onClick={e => {
          if (isClickable) {
            e.stopPropagation();
            openDrawer({
              entityTag,
              earningsId: earningsInsight.earnings_id,
              subtab: isPreview ? SubTabsType.Preview : SubTabsType.Recap,
              source,
            });
          }
        }}
      >
        <EarningsInsightIcon
          entityTag={entityTag}
          size={12}
          earningsInsight={earningsInsight}
          enableHover={false}
          isBordered={false}
          source={source}
        />
        <S.EarningsInsightLabel $isPreview={isPreview}>
          {isPreview
            ? t('earnings:earningsInsight.drawer.earningsPreview.title')
            : t('earnings:earningsInsight.drawer.earningsRecap.title')}
        </S.EarningsInsightLabel>
      </S.EarningsInsightWrapper>
    </TooltipContainer>
  );
};
